import { useState } from "react";
import { csvParse } from "d3";

// Schema-driven replacement for the leadGTab branch of bulk-upload-lead/BulkUploadLead.js.
// CSV headers must cover the common columns (CallerFirstName, CallerLastName,
// CallerPhoneNumber) plus every schema-required field; unknown/extra columns are ignored by
// the server anyway (allowlist filtering happens there too), so no need to reject them here
// beyond a warning.

// fieldType is the schema's declared type for this column ("integer"/"boolean"/etc.), or
// undefined for a common column (CallerFirstName/CallerLastName/CallerPhoneNumber) or an unknown
// column. Only coerces when the schema actually declares that type - never guesses from the raw
// value's shape. A patient_id typed "string" must never get auto-converted to a Number, since
// Number("05821") silently drops the leading zero.
const parseCsvValue = (value, fieldType) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return "";
  if (fieldType === "boolean" && /^(true|false)$/i.test(trimmed)) return trimmed.toLowerCase() === "true";
  if (fieldType === "integer" && /^-?\d+$/.test(trimmed)) return Number(trimmed);
  return trimmed;
};

// Defuses spreadsheet-formula-injection payloads (values starting with =, +, -, @) by prefixing a
// leading apostrophe, so re-exporting this data into Excel/Sheets later can't trigger formula
// execution. Only applies to strings left over from parseCsvValue — numbers/booleans pass through
// untouched since they can't carry a formula prefix.
const sanitizeCsvFormulaValue = (value) => {
  if (typeof value !== "string") return value;
  if (/^[=+\-@]/.test(value)) return `'${value}`;
  return value;
};

const buildTemplateCsv = (schemaFields) => {
  const headers = [
    "CallerFirstName",
    "CallerLastName",
    "CallerPhoneNumber",
    ...schemaFields.map((f) => f.name),
  ];
  return headers.join(",") + "\n";
};

export const DynamicBulkUpload = ({ providerData, schemaFields, onSubmit, submitting }) => {
  const [file, setFile] = useState(null);
  const [providerId, setProviderId] = useState(providerData[0]?.Id ?? "");
  const [errorMsg, setErrorMsg] = useState("");
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewTotalRows, setPreviewTotalRows] = useState(0);
  const [previewMissingColumns, setPreviewMissingColumns] = useState([]);
  const [previewMissingRequiredCount, setPreviewMissingRequiredCount] = useState(0);
  const [previewError, setPreviewError] = useState("");

  const buildPreview = async (selectedFile) => {
    setPreviewError("");
    setPreviewColumns([]);
    setPreviewRows([]);
    setPreviewTotalRows(0);
    setPreviewMissingColumns([]);
    setPreviewMissingRequiredCount(0);

    if (!selectedFile) return;

    try {
      const text = await selectedFile.text();
      const rows = csvParse(text);
      const columns = rows.columns || (rows[0] ? Object.keys(rows[0]) : []);
      const commonColumns = ["CallerFirstName", "CallerLastName", "CallerPhoneNumber"];
      const missingColumns = [
        ...commonColumns.filter((col) => !columns.includes(col)),
        ...schemaFields.filter((f) => f.required && !columns.includes(f.name)).map((f) => f.name),
      ];
      const missingRequiredCount = rows.filter((row) =>
        schemaFields.some((f) => f.required && !String(row[f.name] ?? "").trim()),
      ).length;

      setPreviewColumns(columns);
      setPreviewRows(rows.slice(0, 10));
      setPreviewTotalRows(rows.length);
      setPreviewMissingColumns(missingColumns);
      setPreviewMissingRequiredCount(missingRequiredCount);
    } catch (error) {
      setPreviewError("Could not read this file as CSV.");
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([buildTemplateCsv(schemaFields)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "manage-leads-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (!file || !providerId) return;
    setErrorMsg("");

    const text = await file.text();
    const rows = csvParse(text, (row) => {
      const parsed = {};
      Object.entries(row).forEach(([key, value]) => {
        const field = schemaFields.find((f) => f.name === key);
        parsed[key] = parseCsvValue(value, field?.type);
      });
      return parsed;
    }).filter((row) =>
      Object.values(row).some((cell) => String(cell ?? "").trim() !== ""),
    );

    if (rows.length === 0) {
      setErrorMsg("No valid rows found in file");
      return;
    }

    const missingCommon = rows.some(
      (row) =>
        !("CallerFirstName" in row) ||
        !("CallerLastName" in row) ||
        !("CallerPhoneNumber" in row),
    );
    if (missingCommon) {
      setErrorMsg("CSV must include CallerFirstName, CallerLastName, and CallerPhoneNumber columns");
      return;
    }

    const provider = providerData.find((p) => String(p.Id) === String(providerId));

    const leadsArray = rows.map((row) => {
      const callerInformation = {};
      schemaFields.forEach((f) => {
        const raw = row[f.name];
        if (raw === undefined) return;
        callerInformation[f.name] = sanitizeCsvFormulaValue(raw);
      });
      const callerFirstName = sanitizeCsvFormulaValue(row.CallerFirstName);
      const callerLastName = sanitizeCsvFormulaValue(row.CallerLastName);
      const callerPhoneNumber = String(row.CallerPhoneNumber).replace(/[^0-9]/g, "");
      return {
        CallerFirstName: callerFirstName,
        CallerLastName: callerLastName,
        CallerPhoneNumber: callerPhoneNumber,
        ProviderId: provider?.Id,
        ProviderName: provider?.ProviderName,
        ProviderFacilityName: provider?.ProviderFacilityName,
        CallerInformation: callerInformation,
      };
    });

    const missingRequiredRow = leadsArray.find((lead) =>
      schemaFields.some(
        (f) => f.required && !String(lead.CallerInformation[f.name] ?? "").trim(),
      ),
    );
    if (missingRequiredRow) {
      setErrorMsg(
        "One or more rows are missing a required field — the server will skip those rows and report which ones after upload.",
      );
    }

    await onSubmit(leadsArray);
    setFile(null);
    setPreviewColumns([]);
    setPreviewRows([]);
    setPreviewTotalRows(0);
  };

  return (
    <div>
      <h5>Bulk Upload Leads</h5>
      <p className="text-muted">
        <button className="btn btn-link p-0" onClick={downloadTemplate}>
          Download CSV template
        </button>{" "}
        for this practice's current schema.
      </p>

      {errorMsg && <div className="alert alert-warning">{errorMsg}</div>}

      <div className="mb-2">
        <select
          className="form-select"
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
        >
          {providerData.map((provider) => (
            <option key={provider.Id} value={provider.Id}>
              {provider.ProviderName}
            </option>
          ))}
        </select>
      </div>

      <input
        type="file"
        accept=".csv"
        className="form-control mb-2"
        onChange={(e) => {
          const selectedFile = e.target.files[0];
          setFile(selectedFile);
          buildPreview(selectedFile);
        }}
      />

      {previewError ? (
        <div className="alert alert-warning">{previewError}</div>
      ) : previewColumns.length > 0 ? (
        <div className="mb-2">
          <p className="mb-1">
            Preview - {previewTotalRows} row{previewTotalRows === 1 ? "" : "s"}, {previewColumns.length} column{previewColumns.length === 1 ? "" : "s"}
          </p>
          {previewMissingColumns.length > 0 ? (
            <p className="text-danger mb-1">
              Missing required column{previewMissingColumns.length > 1 ? "s" : ""}: {previewMissingColumns.join(", ")}
            </p>
          ) : (
            <p className="text-success mb-1">All required columns found.</p>
          )}
          {previewMissingRequiredCount > 0 && (
            <p className="text-warning mb-1">
              {previewMissingRequiredCount} row{previewMissingRequiredCount > 1 ? "s" : ""} missing a required field value.
            </p>
          )}
          <div className="table-responsive" style={{ maxHeight: 240, overflow: "auto" }}>
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  {previewColumns.map((col) => <th key={col}>{col}</th>)}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, idx) => (
                  <tr key={idx}>
                    {previewColumns.map((col) => <td key={col}>{String(row[col] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {previewTotalRows > previewRows.length && (
            <p className="text-muted mb-0 mt-1">Showing first {previewRows.length} of {previewTotalRows} rows.</p>
          )}
        </div>
      ) : null}

      <button
        className="simple-btn"
        onClick={handleSubmit}
        disabled={!file || !providerId || submitting}
      >
        Upload
      </button>
    </div>
  );
};
